import type { GridSpec } from './grid.ts';

/**
 * Grid resolution selection.
 *
 * Two grids cover the same area: a fine terrain grid (mesh, slope, avalanche flow) and a
 * coarser snowpack grid (layers, wind transport, stability). Cell sizes are picked from the
 * selected area and a cell budget, and can be overridden. The snowpack cell is always an
 * integer multiple of the terrain cell so snowpack cells map exactly onto terrain blocks.
 */

/** Terrain cell sizes offered (m). 1 m is Kartverket DTM1's native resolution. */
export const TERRAIN_CELL_SIZES = [1, 2, 5, 10, 20, 50] as const;

export interface ResolutionBudget {
  terrainCells: number;
  snowpackCells: number;
}

export const DEFAULT_BUDGET: ResolutionBudget = {
  terrainCells: 1_000_000,
  snowpackCells: 250_000,
};

export interface Resolution {
  /** Terrain cell size (m). */
  terrainCell: number;
  /** Snowpack cell size (m), an integer multiple of `terrainCell`. */
  snowpackCell: number;
}

/** Selected area: a UTM-aligned rectangle given by its centre and size in metres. */
export interface AreaSelection {
  centerX: number;
  centerY: number;
  width: number;
  height: number;
}

/** Smallest offered terrain cell size ≥ `size`; the largest offered size if none is. */
export function snapUpToTerrainCell(size: number): number {
  for (const s of TERRAIN_CELL_SIZES) if (s >= size - 1e-9) return s;
  return TERRAIN_CELL_SIZES[TERRAIN_CELL_SIZES.length - 1]!;
}

export function autoResolution(
  areaM2: number,
  budget: ResolutionBudget = DEFAULT_BUDGET,
): Resolution {
  const terrainCell = snapUpToTerrainCell(Math.max(1, Math.sqrt(areaM2 / budget.terrainCells)));
  const ratio = Math.max(
    1,
    Math.ceil(Math.sqrt(areaM2 / budget.snowpackCells) / terrainCell - 1e-9),
  );
  return { terrainCell, snowpackCell: terrainCell * ratio };
}

export function snowpackRatio(res: Resolution): number {
  return res.snowpackCell / res.terrainCell;
}

export function assertValidResolution(res: Resolution): void {
  if (!(res.terrainCell > 0)) throw new Error(`invalid terrain cell size ${res.terrainCell}`);
  const k = snowpackRatio(res);
  if (!Number.isInteger(k) || k < 1) {
    throw new Error(
      `snowpack cell ${res.snowpackCell} m must be an integer multiple of terrain cell ${res.terrainCell} m`,
    );
  }
}

export interface GridPlan {
  resolution: Resolution;
  terrain: GridSpec;
  snowpack: GridSpec;
}

/**
 * Lay out both grids over the selection. The extent is rounded up to whole snowpack cells
 * (so it may be slightly larger than requested) and stays centred on the selection. The
 * western/northern edges are snapped to the terrain cell size so repeated loads of the same
 * area line up with Kartverket's pixel grid.
 */
export function planGrids(area: AreaSelection, res: Resolution): GridPlan {
  assertValidResolution(res);
  const k = snowpackRatio(res);
  const snowCols = Math.max(1, Math.ceil(area.width / res.snowpackCell - 1e-9));
  const snowRows = Math.max(1, Math.ceil(area.height / res.snowpackCell - 1e-9));
  const width = snowCols * res.snowpackCell;
  const height = snowRows * res.snowpackCell;
  const minX = snap(area.centerX - width / 2, res.terrainCell);
  const maxY = snap(area.centerY + height / 2, res.terrainCell);
  return {
    resolution: res,
    terrain: { cols: snowCols * k, rows: snowRows * k, cellSize: res.terrainCell, minX, maxY },
    snowpack: { cols: snowCols, rows: snowRows, cellSize: res.snowpackCell, minX, maxY },
  };
}

/** Max layers per snowpack column (Phase 3 layered model), used for memory estimates. */
export const MAX_LAYERS = 16;
/** Float32 fields per layer: thickness, density, grain, temperature, lwc, strength, depositedAt. */
const FIELDS_PER_LAYER = 7;
/** Float32s per terrain cell: elevation, slope, aspect + mesh position(3), normal(3), colour(3). */
const FLOATS_PER_TERRAIN_CELL = 12;

export interface MemoryEstimate {
  terrainCells: number;
  snowpackCells: number;
  terrainBytes: number;
  snowpackBytes: number;
  totalBytes: number;
}

export function estimateMemory(plan: GridPlan): MemoryEstimate {
  const terrainCells = plan.terrain.cols * plan.terrain.rows;
  const snowpackCells = plan.snowpack.cols * plan.snowpack.rows;
  const terrainBytes = terrainCells * FLOATS_PER_TERRAIN_CELL * 4;
  const snowpackBytes = snowpackCells * MAX_LAYERS * FIELDS_PER_LAYER * 4;
  return {
    terrainCells,
    snowpackCells,
    terrainBytes,
    snowpackBytes,
    totalBytes: terrainBytes + snowpackBytes,
  };
}

function snap(v: number, step: number): number {
  return Math.round(v / step) * step;
}
